import type { LatLngTuple, Network } from '@tol/core/types';
import { grow, intersects, regionStates } from './viewport.ts';
import type { Box, Camera, RegionView } from './viewport.ts';

export interface RosterNetwork {
  readonly id: string;
  readonly center: readonly [lon: number, lat: number];
  readonly network: Network;
  readonly live: boolean;
}

export interface RosterScope {
  readonly primary: string | null;
  readonly nearby: ReadonlySet<string>;
  readonly primaryLines: ReadonlySet<string>;
}

interface LineCoverage {
  readonly extent: Box;
  readonly paths: readonly (readonly LatLngTuple[])[];
}

const extents = new WeakMap<Network, ReadonlyMap<string, LineCoverage>>();

function lineExtents(network: Network): ReadonlyMap<string, LineCoverage> {
  const cached = extents.get(network);
  if (cached) return cached;
  const found = new Map<string, LineCoverage>();
  for (const line of Object.values(network.lines)) {
    if (!line) continue;
    let west = Infinity;
    let south = Infinity;
    let east = -Infinity;
    let north = -Infinity;
    const include = (lat: number, lon: number): void => {
      west = Math.min(west, lon);
      south = Math.min(south, lat);
      east = Math.max(east, lon);
      north = Math.max(north, lat);
    };
    for (const id of line.ids) {
      const station = network.stations.get(id);
      if (station) include(station.lat, station.lon);
    }
    const paths: (readonly LatLngTuple[])[] = [];
    for (const [a, next] of line.adj) {
      for (const b of next) {
        // The graph is undirected: retain each connection only once.
        if (b < a && line.adj.get(b)?.has(a) === true) continue;
        const pts = network.geom?.get(`${line.id}|${a}|${b}`)?.pts ?? network.geom?.get(`${line.id}|${b}|${a}`)?.pts;
        if (pts && pts.length > 1) {
          paths.push(pts);
          for (const [lat, lon] of pts) include(lat, lon);
        } else {
          const A = network.stations.get(a);
          const B = network.stations.get(b);
          if (A && B)
            paths.push([
              [A.lat, A.lon],
              [B.lat, B.lon],
            ]);
        }
      }
    }
    if (west !== Infinity) found.set(line.id, { extent: [west, south, east, north], paths });
  }
  extents.set(network, found);
  return found;
}

// Clip a segment to the view. A diagonal's enclosing rectangle alone is
// not coverage, and neither endpoint need be visible for a crossing rail.
function segmentReaches(view: Box, a: LatLngTuple, b: LatLngTuple): boolean {
  let enter = 0;
  let exit = 1;
  for (const [from, to, lo, hi] of [
    [a[1], b[1], view[0], view[2]],
    [a[0], b[0], view[1], view[3]],
  ] as const) {
    const delta = to - from;
    if (delta === 0) {
      if (from < lo || from > hi) return false;
    } else {
      const first = (lo - from) / delta;
      const last = (hi - from) / delta;
      enter = Math.max(enter, Math.min(first, last));
      exit = Math.min(exit, Math.max(first, last));
      if (enter > exit) return false;
    }
  }
  return true;
}

function lineReaches(view: Box, network: Network, id: string, coverage: LineCoverage): boolean {
  if (!intersects(view, coverage.extent)) return false;
  for (const stationId of network.lines[id]?.ids ?? []) {
    const station = network.stations.get(stationId);
    if (station && station.lon >= view[0] && station.lon <= view[2] && station.lat >= view[1] && station.lat <= view[3])
      return true;
  }
  for (const path of coverage.paths) {
    for (let i = 1; i < path.length; i++) {
      const a = path[i - 1];
      const b = path[i];
      if (a && b && segmentReaches(view, a, b)) return true;
    }
  }
  return false;
}

const longitudeDistance = (a: number, b: number): number => {
  const distance = Math.abs(a - b) % 360;
  return Math.min(distance, 360 - distance);
};

// Which loaded networks belong in the line list for this view. The nearest
// city's whole roster is shown; overlapping networks contribute only lines
// whose stations or track reach the view. With none, the first
// region whose extension alone reaches the view leads (London's National
// Rail, beyond TfL's London: its rows, clock and count).
export function nearestRegion(camera: Camera, regions: readonly Pick<RosterNetwork, 'id' | 'center'>[]): string | null {
  const lon = (camera.bounds[0] + camera.bounds[2]) / 2;
  const lat = (camera.bounds[1] + camera.bounds[3]) / 2;
  let primary: string | null = null;
  let nearest = Infinity;
  for (const region of regions) {
    const dx = longitudeDistance(lon, region.center[0]) * Math.cos((lat * Math.PI) / 180);
    const dy = lat - region.center[1];
    const distance = dx * dx + dy * dy;
    if (distance < nearest) {
      primary = region.id;
      nearest = distance;
    }
  }
  return primary;
}

// URLs name a region only at its live-track zoom. Polling can also be
// kept running for the primary roster between lines; that is not evidence
// of geographic coverage (a country's extent can span overseas networks).
export function urlRegion(
  camera: Camera,
  regions: readonly (RegionView & Pick<RosterNetwork, 'center'>)[],
): string | null {
  const states = regionStates(camera, regions);
  return nearestRegion(
    camera,
    regions.filter((r) => states.get(r.id) === 'poll'),
  );
}

export function rosterScope(
  camera: Camera,
  regions: readonly RosterNetwork[],
  extended: readonly string[] = [],
  gpsLines: ReadonlySet<string> = new Set(),
  selected?: string | null,
): RosterScope {
  const primary = selected === undefined ? (nearestRegion(camera, regions) ?? extended[0] ?? null) : selected;
  const view = grow(camera.bounds, 0.1);
  const nearby = new Set(gpsLines);
  const primaryLines = new Set<string>();
  for (const region of regions) {
    // A geographically relevant line remains useful before its first feed
    // or with no current vehicles; polling is not a coverage test.
    for (const [lineId, coverage] of lineExtents(region.network))
      if (lineReaches(view, region.network, lineId, coverage))
        (region.id === primary ? primaryLines : nearby).add(lineId);
  }
  return { primary, nearby, primaryLines };
}
